// src/ui/ProfilePanel.jsx — my profile, editable, synced with the phone by using the
// phone's own fields: users/{uid}.fullName / .username / .profileImage, with
// lastUsernameChange as the rate-limit clock. One live listener on my doc means an edit
// made on the phone appears here mid-keystroke, and vice versa.
import React, { useEffect, useRef, useState } from 'react';
import { doc, onSnapshot, updateDoc, getDocs, collection, query, where } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { updateProfile } from 'firebase/auth';
import { db, storage, auth } from '../config/firebase';
import { Camera, Check, Alert, X } from './icons';
/* 🧑‍🎨 the 3D avatar: full body on the profile, the creator, and its removal */
import Avatar3D from './Avatar3D';
import AvatarCreatorModal from './AvatarCreatorModal';
import { hasAvatar } from '../services/avatar3d';
import { removeAvatarFromProfile } from '../services/avatarProfile';
import { setGlobalPrivacy } from '../services/presenceVisibility';
import { legalStatus, setMarketingEmails, LEGAL_URLS } from '../services/legal';   // ⚖️ Terms & Policies
/* 🟥 THIS PANEL RENDERED NOTHING UNTIL ITS FIRST SNAPSHOT — `if (!me) return` — and
   with no network that snapshot never comes, because the web client keeps no
   Firestore persistence. The phone's version of this bug showed the word "User";
   the tab's showed an empty panel. One record, kept on every snapshot, answers both. */
import { selfDisplayName, selfAvatarSource } from '../services/selfIdentity';
import { getSelfIdentity, rememberSelfIdentity } from '../services/selfIdentityWeb';
import {
  ABOUT_VISIBILITY, ABOUT_PRESETS, ABOUT_DURATIONS, MAX_RECENTS,
  aboutFromDoc, buildAboutWrite, clearAboutWrite, expiryLabel, withEmoji, splitStatus,
} from '../services/about';
import EmojiPanel from './EmojiPanel';
import { ProfileDetailsSheet } from './ProfileDetailsKit';   // 🪪 profile details (was "About you")
/* 🔒 the shared reader, so the browser and the phone cannot disagree about what
   "absent" means for a setting that a security rule also reads */
import { friendRequestSetting } from '../services/friendRequests';
/* 🏷 🎂 the same rules the phone's Privacy rows and the server read */
import { taggingSetting, reviewsTags } from '../services/postTags';
import { birthdayVisibility } from '../services/birthdays';
/* 🌍 who sees me on the world map — the rule the server enforces (worldMap.js) */
import { MAP_ONLINE_CHOICES, MAP_VISIBILITIES, mapL, mapOnlineOf, mapVisibilityOf } from '../services/worldMap';
import { refreshAfterSetting as refreshMap } from '../services/worldMapApi';

/** The app's rule surface: username at most twice a month → ≥15 days between changes,
 *  keyed off users/{uid}.lastUsernameChange (the same field the phone writes). */
function usernameGate(last) {
  if (!last) return { ok: true };
  const then = last?.toDate ? last.toDate() : new Date(last);
  const days = (Date.now() - then.getTime()) / 864e5;
  if (days >= 15) return { ok: true };
  return { ok: false, daysLeft: Math.ceil(15 - days) };
}

/* ── GLOBAL PRIVACY — the same users/{uid}.privacySettings the phone's Privacy
   screen owns, so these controls and the phone's are one setting. Per-chat
   switches in Chat info override these for one conversation. ─────────────── */
const LS_OPTS = [
  { id: 'everyone', name: 'Everyone' },
  { id: 'my_contacts', name: 'My contacts' },
  { id: 'nobody', name: 'Nobody' },
];

/* ══ 💬 THE ABOUT STATUS ════════════════════════════════════════════════════
   🟥 THE BROWSER COULD READ ONE AND NOT WRITE ONE. `services/about.js` already
      carried the expiry rule, the privacy rule and the mutual-contacts test —
      all of it so the web could DISPLAY a status somebody had set on their
      phone. There was no way to set one from a keyboard, and no way to end one
      either, which made the web half of a feature.

   🟢 IT WRITES THE PHONE'S OWN FIELDS THROUGH THE PHONE'S OWN BUILDER, so this
      is not a second implementation that happens to agree: `buildAboutWrite`
      here and `buildAboutWrite` in allo-app/services/aboutStatus.ts produce the
      same four fields from the same inputs. The sync is Firestore — the panel
      already listens to `users/{uid}`, so a status set on the phone appears
      here as it is typed, and one set here appears there.

   🟥 AND THE DURATION IS THE PHONE'S LIST, NOT A NICER ONE. The phone re-opens
      its own editor on the duration KEY it stored; a key it does not recognise
      reads as "never expires", so a browser offering "12 hours" would quietly
      produce statuses the phone could neither describe nor re-edit. */
function StatusSection({ myUid, me }) {
  const live = aboutFromDoc(me);
  const [draft, setDraft] = useState('');
  const [dur, setDur] = useState('1d');
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState(false);
  const touched = useRef(false);

  /* 🟢 THE FIELD FOLLOWS THE DOCUMENT UNTIL YOU TOUCH IT — the same rule the
     name and username fields above use, and for the same reason: a status set
     on the phone should appear here, but not by deleting what someone is
     halfway through typing. */
  const serverText = live ? live.text : '';
  useEffect(() => { if (!touched.current) setDraft(serverText); }, [serverText]);

  const recents = (me?.aboutRecents || []).slice(0, MAX_RECENTS);
  const clean = draft.trim();
  const dirty = clean !== serverText;

  const save = async () => {
    setBusy(true);
    try {
      await updateDoc(doc(db, 'users', myUid), buildAboutWrite({
        status: clean, durationKey: dur, recents: me?.aboutRecents,
      }));
      touched.current = false;
    } catch { /* the listener will put back whatever is actually stored */ }
    setBusy(false);
  };

  const clear = async () => {
    setBusy(true);
    try {
      await updateDoc(doc(db, 'users', myUid), clearAboutWrite());
      touched.current = false;
      setDraft('');
    } catch { /* as above */ }
    setBusy(false);
  };

  const type = (v) => { touched.current = true; setDraft(v.slice(0, 40)); };

  return (
    <div className="pp-status">
      <div className="ap-label">Status</div>

      <div className="pp-st-field">
        {/* 🟢 THE OPENER SHOWS WHAT IS CHOSEN, like the phone's does — a face
            when nothing is, the emoji itself when something is. */}
        <button
          className={`pp-st-emojibtn ${picking ? 'on' : ''}`}
          onClick={() => setPicking((v) => !v)}
          aria-expanded={picking}
          title="Choose an emoji"
        >
          {splitStatus(draft).emoji || '🙂'}
        </button>
        <input
          value={draft}
          maxLength={40}
          placeholder="Share a thought!"
          onChange={(e) => type(e.target.value)}
        />
        {clean ? (
          <button className="pp-st-x" title="Clear the box" onClick={() => type('')}>
            <X width={14} height={14} />
          </button>
        ) : null}
      </div>

      {/* 🟥 THE THIRTY-EMOJI STRIP THAT USED TO SIT HERE IS GONE. Thirty is too
          few to find what you meant, and wrapped over four rows beside the field
          it was in the way of the thing it decorates. The picker is its own
          container now, opened from the field, with eight groups and room to
          show them — see EmojiPanel.
          🟥 `withEmoji` REPLACES a leading emoji rather than stacking one in
             front of it, and it knows what an emoji is: stripping the first
             "word" instead would turn "Free to chat" into "😀 to chat". */}
      {picking && (
        <EmojiPanel
          value={splitStatus(draft).emoji}
          onPick={(e) => { type(withEmoji(draft, e)); setPicking(false); }}
          onClose={() => setPicking(false)}
        />
      )}

      <div className="ci-chips pp-st-presets">
        {ABOUT_PRESETS.map((p) => (
          <button key={p.text} className="ci-chip"
            onClick={() => type(`${p.emoji} ${p.text}`)}>
            {p.emoji} {p.text}
          </button>
        ))}
      </div>

      {recents.length > 0 && (
        <>
          <div className="pp-hint">Recent</div>
          <div className="ci-chips pp-st-presets">
            {recents.map((r) => (
              <button key={r} className="ci-chip" onClick={() => type(r)}>{r}</button>
            ))}
          </div>
        </>
      )}

      <div className="ci-pickrow">
        <span className="ci-picklabel">Duration</span>
        <div className="ci-chips">
          {ABOUT_DURATIONS.map((d) => (
            <button key={d.key} className={`ci-chip ${dur === d.key ? 'on' : ''}`}
              onClick={() => setDur(d.key)}>
              {d.label}
            </button>
          ))}
        </div>
      </div>

      <div className="pp-st-actions">
        {clean && dirty && (
          <button className="lg-go slim" disabled={busy} onClick={save}>
            {live ? 'Update status' : 'Set status'}
          </button>
        )}
        {/* 🟥 ENDING A STATUS IS ITS OWN BUTTON, not an empty save somebody has
            to work out. It is offered whenever there is a live status to end —
            including one set on the phone, which is the case that matters. */}
        {live && (
          <button className="pp-st-clear" disabled={busy} onClick={clear}>Clear status</button>
        )}
      </div>

      <div className="pp-hint">
        {live
          ? `Showing “${live.text}” · ${expiryLabel(live.expiresAt)} · also on your phone`
          : 'No status. Whatever you set here shows on your phone too.'}
      </div>
    </div>
  );
}

function PrivacySection({ myUid, me }) {
  const p = me?.privacySettings || {};
  const ls = p.lastSeen || 'everyone';
  const online = p.online || 'same_as_last_seen';
  const rr = p.readReceipts !== false;
  const about = p.aboutVisibility || 'everyone';
  /* 👥 WHO MAY SEE YOU IN A PAGE'S FOLLOWER LIST. Two values only — this is not
     a "who can see" ladder like last seen, it is a single choice about whether
     your name appears in a list anybody can open. `everyone` is the default and
     an unrecognised value reads as `everyone`, matching services/pageFollow.js
     on both clients. */
  const follows = p.followsVisibility === 'nobody' ? 'nobody' : 'everyone';
  /* 🔒 WHO MAY SEND YOU A FRIEND REQUEST. Same two-value shape and the same
     "absent is everyone" default, decided by the shared function rather than by
     a comparison written here — because the security rule has to agree with it
     and three places reading the field three ways is how they stop agreeing. */
  const fr = friendRequestSetting(me);
  const [busy, setBusy] = useState('');

  const set = async (key, value) => {
    setBusy(key);
    try { await setGlobalPrivacy(myUid, { [key]: value }); } catch {}
    /* 🌍 the map re-reads the setting — moved, named or removed now, not at the next refresh */
    if (key === 'mapVisibility' || key === 'mapOnline') { try { await refreshMap(); } catch {} }
    setBusy('');
  };

  return (
    <div className="pp-privacy">
      <div className="ap-label">Privacy</div>

      <div className="ci-pickrow">
        <span className="ci-picklabel">Last seen</span>
        <div className="ci-chips">
          {LS_OPTS.map((o) => (
            <button key={o.id} className={`ci-chip ${ls === o.id ? 'on' : ''}`}
              disabled={busy === 'lastSeen'} onClick={() => set('lastSeen', o.id)}>
              {o.name}
            </button>
          ))}
          {/* the phone's "except N people" list — shown, honoured, and left to the
              phone to edit rather than half-rebuilt here */}
          {ls === 'except' && (
            <span className="ci-chip on">
              Everyone except {(p.lastSeenExcept || []).length}
            </span>
          )}
        </div>
      </div>

      {/* 💬 WHO CAN SEE YOUR ABOUT — the same users/{uid}.privacySettings.aboutVisibility
          the phone's Privacy screen owns, so changing it here moves the phone's
          switch. The status itself is written from the phone; this decides who
          it reaches, and that decision is honoured on both clients. */}
      <div className="ci-pickrow">
        <span className="ci-picklabel">About status</span>
        <div className="ci-chips">
          {ABOUT_VISIBILITY.map((o) => (
            <button key={o.id} className={`ci-chip ${about === o.id ? 'on' : ''}`}
              disabled={busy === 'aboutVisibility'} onClick={() => set('aboutVisibility', o.id)}>
              {o.label}
            </button>
          ))}
          {/* 🟢 the excluded list is shown and honoured, and left to the phone to
              edit — the same call the last-seen row above makes, for the same
              reason: a contact picker rebuilt here would be a second answer to
              who is in it */}
          {about === 'except' && (
            <span className="ci-chip on">
              {(p.aboutVisibilityExcept || []).length} excluded
            </span>
          )}
        </div>
      </div>

      {/* 👥 FOLLOWERS — the same users/{uid}.privacySettings.followsVisibility the
          phone reads. It hides your NAME from the public list; it does not
          un-follow anything and it does not hide you from the page's owner or
          from Allo, because following is a write to their document. Saying that
          plainly is better than a switch people read as "follow secretly". */}
      <div className="ci-pickrow">
        <span className="ci-picklabel">Follower lists</span>
        <div className="ci-chips">
          <button className={`ci-chip ${follows === 'everyone' ? 'on' : ''}`}
            disabled={busy === 'followsVisibility'} onClick={() => set('followsVisibility', 'everyone')}>
            Show me
          </button>
          <button className={`ci-chip ${follows === 'nobody' ? 'on' : ''}`}
            disabled={busy === 'followsVisibility'} onClick={() => set('followsVisibility', 'nobody')}>
            Hide me
          </button>
        </div>
      </div>
      {follows === 'nobody' && (
        <div className="pp-note">
          You still count towards a page’s follower number, and the page’s owner can still see you.
        </div>
      )}

      {/* 🔒 FRIEND REQUESTS. Off means nobody can send you one, and that is
          enforced by the rule on the collection as well as by both apps —
          otherwise it would only be a hidden button. */}
      <div className="ci-pickrow">
        <span className="ci-picklabel">Friend requests</span>
        <div className="ci-chips">
          <button className={`ci-chip ${fr === 'everyone' ? 'on' : ''}`}
            disabled={busy === 'friendRequests'} onClick={() => set('friendRequests', 'everyone')}>
            Anyone
          </button>
          <button className={`ci-chip ${fr === 'nobody' ? 'on' : ''}`}
            disabled={busy === 'friendRequests'} onClick={() => set('friendRequests', 'nobody')}>
            No one
          </button>
        </div>
      </div>
      {fr === 'nobody' && (
        <div className="pp-note">
          People already on your friends list stay there. This only stops new requests.
        </div>
      )}

      {/* 🏷 TAGS — who may tag me, and whether a tag waits for my yes. Decided by
          the server when somebody posts (tagRoutes.js), so it holds on every client. */}
      <div className="ci-pickrow">
        <span className="ci-picklabel">Who can tag you</span>
        <div className="ci-chips">
          {[['everyone', 'Everyone'], ['friends', 'Friends'], ['nobody', 'No one']].map(([id, label]) => (
            <button key={id} className={`ci-chip ${taggingSetting(me) === id ? 'on' : ''}`}
              disabled={busy === 'tagging'} onClick={() => set('tagging', id)}>{label}</button>
          ))}
        </div>
      </div>
      <div className="ci-pickrow">
        <span className="ci-picklabel">Review tags</span>
        <div className="ci-chips">
          <button className={`ci-chip ${!reviewsTags(me) ? 'on' : ''}`} disabled={busy === 'tagReview'} onClick={() => set('tagReview', false)}>Show at once</button>
          <button className={`ci-chip ${reviewsTags(me) ? 'on' : ''}`} disabled={busy === 'tagReview'} onClick={() => set('tagReview', true)}>Ask me first</button>
        </div>
      </div>

      {/* 🌍 THE WORLD MAP — the default (absent) is a nameless dot */}
      <div className="ci-pickrow">
        <span className="ci-picklabel">{mapL('who_sees', navigator.language)}</span>
        <div className="ci-chips">
          {MAP_VISIBILITIES.map((id) => (
            <button key={id} className={`ci-chip ${mapVisibilityOf(me) === id ? 'on' : ''}`}
              disabled={busy === 'mapVisibility'} onClick={() => set('mapVisibility', id)}>{mapL(`vis_${id}`, navigator.language)}</button>
          ))}
        </div>
      </div>
      <div className="pp-note">{mapL(`vis_${mapVisibilityOf(me)}_sub`, navigator.language)}</div>
      {/* 🟢 ONLINE ON THE MAP — unset, it follows Online / Last seen */}
      <div className="ci-pickrow">
        <span className="ci-picklabel">{mapL('show_online', navigator.language)}</span>
        <div className="ci-chips">
          {MAP_ONLINE_CHOICES.map((id) => (
            <button key={id} className={`ci-chip ${mapOnlineOf(me) === id ? 'on' : ''}`}
              disabled={busy === 'mapOnline'} onClick={() => set('mapOnline', id)}>{mapL(id === 'nobody' ? 'vis_nobody' : `on_${id}`, navigator.language)}</button>
          ))}
        </div>
      </div>
      <div className="pp-note">{mapL(`on_${mapOnlineOf(me)}_sub`, navigator.language)}</div>

      {/* 🎂 BIRTHDAYS — only the day is ever shown, never the year or the age */}
      <div className="ci-pickrow">
        <span className="ci-picklabel">Birthday visible to</span>
        <div className="ci-chips">
          {[['everyone', 'Everyone'], ['friends', 'Friends'], ['nobody', 'Only me']].map(([id, label]) => (
            <button key={id} className={`ci-chip ${birthdayVisibility(me) === id ? 'on' : ''}`}
              disabled={busy === 'birthdayVisibility'} onClick={() => set('birthdayVisibility', id)}>{label}</button>
          ))}
        </div>
      </div>
      <div className="ci-pickrow">
        <span className="ci-picklabel">Tell friends on my birthday</span>
        <div className="ci-chips">
          <button className={`ci-chip ${p.birthdayAnnounce !== false ? 'on' : ''}`} disabled={busy === 'birthdayAnnounce' || birthdayVisibility(me) === 'nobody'} onClick={() => set('birthdayAnnounce', true)}>On</button>
          <button className={`ci-chip ${p.birthdayAnnounce === false ? 'on' : ''}`} disabled={busy === 'birthdayAnnounce'} onClick={() => set('birthdayAnnounce', false)}>Off</button>
        </div>
      </div>
      <div className="ci-pickrow">
        <span className="ci-picklabel">Friends' birthdays</span>
        <div className="ci-chips">
          <button className={`ci-chip ${p.birthdayAlerts !== false ? 'on' : ''}`} disabled={busy === 'birthdayAlerts'} onClick={() => set('birthdayAlerts', true)}>Notify me</button>
          <button className={`ci-chip ${p.birthdayAlerts === false ? 'on' : ''}`} disabled={busy === 'birthdayAlerts'} onClick={() => set('birthdayAlerts', false)}>Don't</button>
        </div>
      </div>

      <div className="ci-pickrow">
        <span className="ci-picklabel">Online</span>
        <div className="ci-chips">
          <button className={`ci-chip ${online === 'everyone' ? 'on' : ''}`}
            disabled={busy === 'online'} onClick={() => set('online', 'everyone')}>
            Everyone
          </button>
          <button className={`ci-chip ${online === 'same_as_last_seen' ? 'on' : ''}`}
            disabled={busy === 'online'} onClick={() => set('online', 'same_as_last_seen')}>
            Same as last seen
          </button>
        </div>
      </div>

      {/* 🟥 THE GLOBAL READ-RECEIPTS SWITCH — one setting, two clients.
          It writes users/{uid}.privacySettings.readReceipts, the exact field the
          phone's Privacy screen owns, so flipping it here moves the phone's
          switch and vice versa. Everything that could report a read resolves
          against it: this chat's own override first, then this.

          Deliberately NOT claiming "and you won't see theirs either". The phone's
          own description says that, but neither client actually suppresses
          INCOMING receipts — the gate is on sending, in both. Saying it here
          would be a promise the product doesn't keep. */}
      <div className="ap-toggle-row">
        <div>
          Read receipts
          <i>
            {rr
              ? 'People see when you have read their messages, in chats and groups'
              : 'Off — nobody sees when you have read their messages, including in groups'}
          </i>
        </div>
        <button className={`ap-switch ${rr ? 'on' : ''}`} role="switch" aria-checked={rr}
          aria-label="Read receipts" onClick={() => set('readReceipts', !rr)} />
      </div>

      {!rr && (
        <div className="pp-note">
          <Check width={13} height={13} />
          <span>
            <b>Mark all read</b> won&rsquo;t turn anyone&rsquo;s ticks green while this is off —
            here or on your phone. Your own badges still clear.
          </span>
        </div>
      )}

      <div className="pp-hint">
        If you don’t share your last seen, you won’t see other people’s either. Any chat can
        override these in its own Chat info.
      </div>
    </div>
  );
}

export default function ProfilePanel({ myUid }) {
  /* 🟢 localStorage is not a round trip, so this is a lazy initialiser: the panel
     starts from what it knew rather than from nothing, and `if (!me) return` — the
     line that produced the blank panel offline — stops being reachable for an
     account that has ever been online. The snapshot overwrites it the moment one
     arrives; until then the fields it does not carry are simply absent, which is a
     shape every reader below already handles. */
  const [kept] = useState(() => getSelfIdentity(myUid));
  const [me, setMe] = useState(() => {
    const k = getSelfIdentity(myUid);
    return k ? { uid: myUid, fullName: k.fullName, username: k.username, profileImage: k.photo } : null;
  });
  const [name, setName] = useState('');
  const [uname, setUname] = useState('');
  const [busy, setBusy] = useState(false);
  const [showCreator, setShowCreator] = useState(false);   // 🧑‍🎨 the avatar creator modal
  const [detailsOpen, setDetailsOpen] = useState(false);   // 🪪 profile details editor
  const [msg, setMsg] = useState(null);       // { ok, text }
  const fileRef = useRef(null);
  const editedRef = useRef({ name: false, uname: false });

  useEffect(() => onSnapshot(doc(db, 'users', myUid), (s) => {
    const d = s.data() || {};
    setMe({ ...d, uid: myUid });
    rememberSelfIdentity(myUid, d, auth.currentUser);
    // live sync from the phone — but never overwrite what I'm mid-typing
    if (!editedRef.current.name) setName(d.fullName || '');
    if (!editedRef.current.uname) setUname(d.username || '');
  }), [myUid]);

  if (!me) return <div className="calls-panel"><div className="cp-head"><b>Profile</b></div></div>;

  const gate = usernameGate(me.lastUsernameChange);

  const saveName = async () => {
    const clean = name.trim();
    if (!clean || clean === me.fullName) return;
    setBusy(true); setMsg(null);
    try {
      await updateDoc(doc(db, 'users', myUid), { fullName: clean, updatedAt: new Date() });
      editedRef.current.name = false;
      setMsg({ ok: true, text: 'Name updated — your phone will show it too.' });
    } catch { setMsg({ ok: false, text: 'Couldn’t save the name. Try again.' }); }
    setBusy(false);
  };

  const saveUsername = async () => {
    const clean = uname.trim().toLowerCase();
    if (!clean || clean === me.username) return;
    if (!/^[a-z0-9_.]{3,24}$/.test(clean)) {
      setMsg({ ok: false, text: 'Usernames are 3–24 characters: letters, numbers, dot, underscore.' });
      return;
    }
    if (!gate.ok) {
      setMsg({ ok: false, text: `You can change your username twice a month — next change in ${gate.daysLeft} day${gate.daysLeft === 1 ? '' : 's'}.` });
      return;
    }
    setBusy(true); setMsg(null);
    try {
      // the app's uniqueness check, verbatim: a query, taken if non-empty and not me
      const taken = await getDocs(query(collection(db, 'users'), where('username', '==', clean)));
      if (taken.docs.some((d) => d.id !== myUid)) {
        setMsg({ ok: false, text: 'That username is taken.' });
        setBusy(false);
        return;
      }
      await updateDoc(doc(db, 'users', myUid), {
        username: clean, lastUsernameChange: new Date(), updatedAt: new Date(),
      });
      editedRef.current.uname = false;
      setMsg({ ok: true, text: 'Username updated on every device.' });
    } catch { setMsg({ ok: false, text: 'Couldn’t save the username. Try again.' }); }
    setBusy(false);
  };

  const pickPhoto = () => fileRef.current?.click();

  /* 🧑‍🎨 the avatar (its hook is declared above, before the early return) */
  const removeAvatar = async () => {
    if (!window.confirm('Remove your avatar? Your previous profile picture comes back.')) return;
    setBusy(true);
    try { await removeAvatarFromProfile(myUid, me); setMsg({ ok: true, text: 'Avatar removed.' }); }
    catch { setMsg({ ok: false, text: 'Couldn’t remove the avatar. Try again.' }); }
    setBusy(false);
  };
  const uploadPhoto = async (f) => {
    if (!f) return;
    setBusy(true); setMsg(null);
    try {
      // the app's exact path — a fixed name, overwritten, so the phone's URL stays valid
      const r = ref(storage, `profileImages/${myUid}/profile.jpg`);
      await uploadBytes(r, f, { contentType: f.type || 'image/jpeg' });
      const url = await getDownloadURL(r);
      if (auth.currentUser) await updateProfile(auth.currentUser, { photoURL: url }).catch(() => {});
      await updateDoc(doc(db, 'users', myUid), { profileImage: url, updatedAt: new Date() });
      // the app also re-stamps the group-chat avatar map — parity matters here, or
      // groups would keep showing the old face to everyone until the phone next did it
      try {
        const gs = await getDocs(query(collection(db, 'chats'), where('participants', 'array-contains', myUid)));
        await Promise.all(gs.docs.filter((d) => d.data()?.isGroup).map((d) =>
          updateDoc(d.ref, { [`participantImages.${myUid}`]: url }).catch(() => {})));
      } catch { /* best effort, same as the app */ }
      setMsg({ ok: true, text: 'Photo updated.' });
    } catch { setMsg({ ok: false, text: 'Upload failed. Try a smaller image.' }); }
    setBusy(false);
  };

  return (
    <div className="calls-panel">
      <div className="cp-head"><b>Profile</b></div>
      <div className="pp-body">
        {hasAvatar(me) ? (
          /* 🧑‍🎨 the whole figure, rollable — drag to turn it around */
          <div className="pp-avatar3d">
            <Avatar3D avatar={me.avatar} mode="fullbody" width={200} height={260} interactive returnAfterMs={0} />
            <button className="pp-cam" onClick={pickPhoto} title="Change photo"><Camera width={14} height={14} /></button>
          </div>
        ) : (
        <button className="pp-photo" onClick={pickPhoto} title="Change photo">
          {selfAvatarSource(me, auth.currentUser, kept)
            ? <img src={selfAvatarSource(me, auth.currentUser, kept)} alt="" />
            : <span className="pp-ph">{selfDisplayName(me, auth.currentUser, kept, '?').trim().charAt(0).toUpperCase()}</span>}
          <span className="pp-cam"><Camera width={14} height={14} /></span>
        </button>
        )}
        <div className="pp-av-actions">
          <button className="btn ghost" disabled={busy} onClick={() => setShowCreator(true)}>{hasAvatar(me) ? 'Edit avatar' : 'Create avatar'}</button>
          {hasAvatar(me) && <button className="btn danger" disabled={busy} onClick={removeAvatar}>Remove avatar</button>}
        </div>
        {showCreator && <AvatarCreatorModal myUid={myUid} profile={me} onClose={() => setShowCreator(false)} />}
        <input ref={fileRef} type="file" accept="image/*" hidden
          onChange={(e) => { uploadPhoto(e.target.files?.[0]); e.target.value = ''; }} />

        {msg && (
          <div className={msg.ok ? 'lg-note' : 'lg-err'}>
            {msg.ok ? <Check width={15} height={15} /> : <Alert width={15} height={15} />}{msg.text}
          </div>
        )}

        <label className="lg-field">
          <span className="lg-label">Name</span>
          <span className="lg-input">
            <input value={name} placeholder="Your name"
              onChange={(e) => { editedRef.current.name = true; setName(e.target.value); }} />
          </span>
        </label>
        {name.trim() && name.trim() !== me.fullName && (
          <button className="lg-go" disabled={busy} onClick={saveName}>Save name</button>
        )}

        <label className="lg-field" style={{ marginTop: 14 }}>
          <span className="lg-label">Username</span>
          <span className="lg-input">
            <span className="pp-at">@</span>
            <input value={uname} placeholder="username"
              onChange={(e) => { editedRef.current.uname = true; setUname(e.target.value); }} />
          </span>
        </label>
        <div className="pp-hint">
          {gate.ok
            ? 'You can change your username twice a month.'
            : `Next username change available in ${gate.daysLeft} day${gate.daysLeft === 1 ? '' : 's'}.`}
        </div>
        {uname.trim() && uname.trim().toLowerCase() !== me.username && (
          <button className="lg-go" disabled={busy || !gate.ok} onClick={saveUsername}>Save username</button>
        )}

        <div className="pp-meta">
          {me.phoneE164 && <div><span>Phone</span><b>{me.phoneE164}</b></div>}
          {me.email && <div><span>Email</span><b>{me.email}</b></div>}
        </div>

        <StatusSection myUid={myUid} me={me} />

        {/* 🪪 "ABOUT YOU" IS NOW "PROFILE DETAILS", and it lives on your profile
            (work, school, places, links — each line with its own audience).
            This is a way in from settings. */}
        <div className="pp-section">
          <button type="button" className="pd-btn wide" onClick={() => setDetailsOpen(true)}>Profile details</button>
        </div>
        {detailsOpen && <ProfileDetailsSheet uid={myUid} isOwner startEditing onClose={() => setDetailsOpen(false)} />}

        <PrivacySection myUid={myUid} me={me} />
        {/* ⚖️ the Terms, the Privacy Policy, email choices, my data — as on the phone (Settings → Terms & Policies) */}
        <LegalSection />
      </div>
    </div>
  );
}

/* ⚖️ TERMS & POLICIES. The documents open as pages (public/legal); "Product news & offers" is the
   server's switch (services/legal) — the same one the phone's Settings shows. */
function LegalSection() {
  const [mkt, setMkt] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let live = true;
    legalStatus().then((r) => { if (live && r.ok) setMkt(!!r.marketingEmails); });
    return () => { live = false; };
  }, []);
  const toggle = async () => {
    if (busy || mkt === null) return;
    const next = !mkt;
    setMkt(next); setBusy(true);
    const r = await setMarketingEmails(next);
    setBusy(false);
    if (!r.ok) setMkt(!next);
  };
  return (
    <div className="pp-privacy">
      <div className="ap-label">Terms &amp; Policies</div>
      <div className="ci-pickrow">
        <span className="ci-picklabel">Product news &amp; offers by email</span>
        <div className="ci-chips">
          <button className={`ci-chip ${mkt === true ? 'on' : ''}`} disabled={busy || mkt === null} onClick={() => { if (!mkt) toggle(); }}>On</button>
          <button className={`ci-chip ${mkt === false ? 'on' : ''}`} disabled={busy || mkt === null} onClick={() => { if (mkt) toggle(); }}>Off</button>
        </div>
      </div>
      <div className="pp-note">Sign-in codes, password resets and security emails are part of your account and are always sent.</div>
      <div className="pp-note">
        <a href={LEGAL_URLS.terms} target="_blank" rel="noopener noreferrer">Terms of Service</a>
        {' · '}<a href={LEGAL_URLS.privacy} target="_blank" rel="noopener noreferrer">Privacy Policy</a>
        {' · '}<a href={LEGAL_URLS.guidelines} target="_blank" rel="noopener noreferrer">Community Guidelines</a>
        {' · '}<a href={LEGAL_URLS.cookies} target="_blank" rel="noopener noreferrer">Cookies</a>
        {' · '}<a href={LEGAL_URLS.deleteAccount} target="_blank" rel="noopener noreferrer">Delete my account</a>
      </div>
    </div>
  );
}
